const BLOCKED_TAGS = new Set(["script", "foreignobject", "iframe", "object", "embed"]);

export type SanitizedSvgAsset = {
  content: string;
  viewBox: string;
};

export function sanitizeInlineSvg(raw: string): SanitizedSvgAsset {
  const source = raw.trim();
  if (!source) {
    throw new Error("SVG markup is required.");
  }
  if (typeof window === "undefined" || typeof DOMParser === "undefined" || typeof XMLSerializer === "undefined") {
    throw new Error("SVG import is only available in the browser.");
  }

  const parsed = new DOMParser().parseFromString(source, "image/svg+xml");
  const root = parsed.documentElement;
  if (!root || root.tagName.toLowerCase() !== "svg") {
    throw new Error("SVG markup must start with a single <svg> root element.");
  }

  const invalid = parsed.querySelector("parsererror");
  if (invalid) {
    throw new Error("Could not parse the SVG markup.");
  }

  const visit = (element: Element) => {
    const tagName = element.tagName.toLowerCase();
    if (BLOCKED_TAGS.has(tagName)) {
      element.remove();
      return;
    }

    for (const attribute of [...element.attributes]) {
      const name = attribute.name.toLowerCase();
      const value = attribute.value;
      if (name.startsWith("on")) {
        element.removeAttribute(attribute.name);
        continue;
      }
      if ((name === "href" || name === "xlink:href") && /^\s*javascript:/i.test(value)) {
        element.removeAttribute(attribute.name);
      }
    }

    for (const child of [...element.children]) {
      visit(child);
    }
  };

  visit(root);

  root.removeAttribute("width");
  root.removeAttribute("height");
  root.setAttribute("xmlns", "http://www.w3.org/2000/svg");
  root.setAttribute("preserveAspectRatio", root.getAttribute("preserveAspectRatio") || "xMidYMid meet");

  const viewBox = root.getAttribute("viewBox");
  if (!viewBox) {
    throw new Error("SVG must include a viewBox so it can scale safely on the canvas.");
  }

  return {
    content: new XMLSerializer().serializeToString(root),
    viewBox,
  };
}
